import axios from "axios";

const apiBase = process.env.REACT_APP_NOT_SECRET_CODE || "";

const authHeaders = (token, contentType = "application/json") => ({
  "Content-Type": contentType,
  "X-Request-ID": "124",
  "X-Country-Code": "BD",
  "X-Client-Name": "WEBSITE",
  "X-Client-Version": "123",
  Authorization: "Bearer " + token,
});

const unwrap = (res) => res.data;

export const createPackageLeadImport = (token, payload) => {
  return axios
    .post(`${apiBase}/package-lead-import/create`, payload, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

const packageOnlyPost = (path, token, payload) => axios
  .post(`${apiBase}/package-lead-import/${path}`, payload, { headers: authHeaders(token) })
  .then(unwrap);

export const createPackageOnlyImport = (token, payload) =>
  packageOnlyPost("package-only/create", token, payload);
export const uploadPackageOnlyRowsChunk = (token, payload) =>
  packageOnlyPost("package-only/rows-chunk", token, payload);
export const applyPackageOnlyImport = (token, payload) =>
  packageOnlyPost("package-only/apply", token, payload);
export const movePackageLeadsToBasic = (token, payload) =>
  packageOnlyPost("move-to-basic", token, payload);
export const resumePackageLeadOperation = (token, payload) =>
  packageOnlyPost("resume", token, payload);
export const deletePackageLeadTask = (token, payload) =>
  packageOnlyPost("delete-task", token, payload);
export const downloadPackageSkippedReport = (token, importId) => axios
  .get(`${apiBase}/package-lead-import/skipped-report`, {
    headers: authHeaders(token), params: { import_id: importId }, responseType: "blob",
  }).then((res) => res.data);

export const uploadPackageLeadImport = (token, file, packageId, onProgress) => {
  const formData = new FormData();
  formData.append("file", file);
  if (packageId) {
    formData.append("package_id", packageId);
  }

  return axios
    .post(`${apiBase}/package-lead-import/upload`, formData, {
      headers: authHeaders(token, "multipart/form-data"),
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    })
    .then(unwrap);
};

const chunkSize = 1024 * 1024;

const uploadPackageLeadImportChunk = (token, formData) => {
  return axios
    .post(`${apiBase}/package-lead-import/upload-chunk`, formData, {
      headers: authHeaders(token, "multipart/form-data"),
    })
    .then(unwrap);
};

const completePackageLeadImportUpload = (token, payload) => {
  return axios
    .post(`${apiBase}/package-lead-import/complete-upload`, payload, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

export const uploadPackageLeadImportChunked = async (token, file, packageId, onProgress) => {
  const uploadId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const totalChunks = Math.ceil(file.size / chunkSize);

  for (let index = 0; index < totalChunks; index += 1) {
    const start = index * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const formData = new FormData();
    formData.append("chunk", file.slice(start, end), file.name);
    formData.append("upload_id", uploadId);
    formData.append("chunk_index", index);
    formData.append("total_chunks", totalChunks);
    await uploadPackageLeadImportChunk(token, formData);
    if (onProgress) {
      onProgress(Math.round(((index + 1) / totalChunks) * 90));
    }
  }

  const payload = {
    upload_id: uploadId,
    total_chunks: totalChunks,
    file_name: file.name,
  };
  if (packageId) {
    payload.package_id = packageId;
  }

  const result = await completePackageLeadImportUpload(token, payload);
  if (onProgress) {
    onProgress(100);
  }

  return result;
};

export const uploadPackageLeadImportLegacy = (token, file, packageId) => {
  const formData = new FormData();
  formData.append("file", file);
  if (packageId) {
    formData.append("package_id", packageId);
  }

  return axios
    .post(`${apiBase}/package-lead-import/upload`, formData, {
      headers: authHeaders(token, "multipart/form-data"),
    })
    .then(unwrap);
};

export const previewPackageLeadImport = (token, payload) => {
  return axios
    .post(`${apiBase}/package-lead-import/preview`, payload, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

export const applyPackageLeadImport = (token, payload) => {
  return axios
    .post(`${apiBase}/package-lead-import/apply`, payload, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

export const uploadPackageLeadImportRowsChunk = (token, payload) => {
  return axios
    .post(`${apiBase}/package-lead-import/rows-chunk`, payload, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

export const applyPackageLeadImportStoredRows = (token, payload) => {
  return axios
    .post(`${apiBase}/package-lead-import/apply-stored-rows`, payload, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

export const getPackageLeadImportStatus = (token, importId) => {
  return axios
    .get(`${apiBase}/package-lead-import/status/${importId}`, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

export const getPackageLeadImportHistory = (token, limit = 20) => {
  return axios
    .get(`${apiBase}/package-lead-import/history?limit=${limit}`, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

export const getPackagesWithoutPagination = (token) => {
  return axios
    .get(`${apiBase}/packageWithoutPagination`, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

export const getPackageOrganizations = (token, packageId) => {
  return axios
    .get(`${apiBase}/cataloguePackage/organization/assign/${packageId}`, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

export const uploadPackageLeadImportV2 = (token, file, packageId, packageEndDate, onProgress) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("package_id", packageId);
  if (packageEndDate) {
    formData.append("package_end_date", packageEndDate);
  }

  return axios
    .post(`${apiBase}/package-lead-import-v2/upload`, formData, {
      headers: authHeaders(token, "multipart/form-data"),
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    })
    .then(unwrap);
};

export const previewPackageLeadImportV2 = (token, importId, mapping) => {
  return axios
    .post(`${apiBase}/package-lead-import-v2/${importId}/preview`, { mapping }, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

export const applyPackageLeadImportV2 = (token, importId) => {
  return axios
    .post(`${apiBase}/package-lead-import-v2/${importId}/apply`, {}, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

export const getPackageLeadImportV2Status = (token, importId) => {
  return axios
    .get(`${apiBase}/package-lead-import-v2/${importId}/status`, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

export const getPackageLeadImportV2FailedRows = (token, importId, limit = 100) => {
  return axios
    .get(`${apiBase}/package-lead-import-v2/${importId}/failed-rows?limit=${limit}`, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};

export const retryPackageLeadImportV2Wallets = (token, importId) => {
  return axios
    .post(`${apiBase}/package-lead-import-v2/${importId}/retry-wallets`, {}, {
      headers: authHeaders(token),
    })
    .then(unwrap);
};
